import { makeObservable, observable } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { ApiError } from '/src/api';
import { Company, Lender, Loan } from '/src/lib/models';

export interface HeaderStoreProps {
  /**
   * Whether to render the page Header
   *
   * @remarks
   * Used on the Login and Password Reset pages
   */
  renderHeader: boolean;
  /**
   * Name of the current page
   */
  currentPage: string;
}

export class HeaderStore extends BaseStore implements HeaderStoreProps {
  renderHeader = true;
  currentPage = '';

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      renderHeader: observable,
      currentPage: observable,
    });
  }

  /**
   * Fetches a list of Companies and populates the Company filters with the result.
   */
  loadCompanies = async () => {
    if (!this.rootStore.userStore.user) return;

    try {
      const response = await this.rootStore.companyStore.query();

      const companyList = response?.map((company: Company) => ({
        ...company,
        tags: {
          companies: [company._id],
          loans: company.loans ? company.loans.map((loan) => loan._id) : [],
          lenders: [],
        },
      }));

      this.rootStore.filterStore.companyListSet(companyList);
    } catch (err) {
      if ((err as ApiError).status === 401) {
        const errBody = (err as ApiError).body as Record<string, any>;
        if (errBody.message?.toLocaleLowerCase() === 'user is not logged in') {
          this.rootStore.userStore.signout();
        }
      } else {
        console.error(err);
      }
    }
  };

  /**
   * Fetches a list of Loans and populates the Loan filters with the result.
   */
  loadLoans = async () => {
    if (!this.rootStore.userStore.user) return;

    try {
      const response = await this.rootStore.loanStore.query();

      const loanList = response?.map((loan: Loan) => ({
        ...loan,
        tags: {
          loans: [loan.borrower._id],
          loanList: [loan._id],
          lenders: loan.lenders.map((lender) => lender._id),
        },
      }));

      this.rootStore.filterStore.loanListSet(loanList);
    } catch (err) {
      if ((err as ApiError).status === 401) {
        const errBody = (err as ApiError).body as Record<string, any>;
        if (errBody.message?.toLocaleLowerCase() === 'user is not logged in') {
          this.rootStore.userStore.signout();
        }
      } else {
        console.error(err);
      }
    }
  };

  /**
   * Fetches a list of Lenders and populates the Lender filters with the result.
   */
  loadLenders = async () => {
    if (!this.rootStore.userStore.user) return;

    try {
      const response = await this.rootStore.lenderStore.query();

      const lenderList = response?.map((lender: Lender) => ({
        ...lender,
        tags: {
          companies: [],
          loans: [],
          lenders: [lender._id],
        },
      }));

      this.rootStore.filterStore.lenderListSet(lenderList);
    } catch (err) {
      if ((err as ApiError).status === 401) {
        const errBody = (err as ApiError).body as Record<string, any>;
        if (errBody.message?.toLocaleLowerCase() === 'user is not logged in') {
          this.rootStore.userStore.signout();
        }
      } else {
        console.error(err);
      }
    }
  };
}
